<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, type Component } from 'vue';
import { useI18n } from 'vue-i18n';
import { RouterLink, useRoute } from 'vue-router';

import {
  Activity,
  CalendarDays,
  ChevronDown,
  ClipboardList,
  FileText,
  Folder,
  Home,
  LayoutGrid,
  LogOut,
  Menu,
  Shield,
  Sparkles,
  User,
  Users,
} from 'lucide-vue-next';

import logoZnak from '@/assets/logo-znak.svg';
import { apiClient } from '@/api/client.js';
import { useAuthStore } from '@/stores/auth.js';

const { t } = useI18n();
const authStore = useAuthStore();
const route = useRoute();

// Баннер «подтвердите email» + повторная отправка письма.
const resending = ref(false);
const resent = ref(false);
async function resendVerification() {
  if (resending.value || resent.value) return;
  resending.value = true;
  try {
    await apiClient.post('/api/v1/auth/resend-verification');
    resent.value = true;
  } catch {
    // молча — не критично, пользователь может повторить позже
  } finally {
    resending.value = false;
  }
}

interface NavItem {
  to: string;
  icon: Component;
  labelKey: string;
}

/** Нижний таб-бар (только мобилка) — основные разделы. */
const tabItems: NavItem[] = [
  { to: '/', icon: Home, labelKey: 'nav.dashboard' },
  { to: '/events', icon: CalendarDays, labelKey: 'nav.events' },
  { to: '/services', icon: LayoutGrid, labelKey: 'nav.services' },
  { to: '/profile', icon: User, labelKey: 'nav.profile' },
];

/** Под-навигация админки (только на /admin). */
const adminNav: NavItem[] = [
  { to: '/admin/services', icon: Folder, labelKey: 'admin.nav.services' },
  { to: '/admin/users', icon: Users, labelKey: 'admin.nav.users' },
  { to: '/admin/groups', icon: Shield, labelKey: 'admin.nav.groups' },
  { to: '/admin/content', icon: Sparkles, labelKey: 'admin.nav.content' },
  { to: '/admin/consent', icon: FileText, labelKey: 'admin.nav.consent' },
  { to: '/admin/audit', icon: ClipboardList, labelKey: 'admin.nav.audit' },
];

const isAdmin = computed(() => authStore.isAdmin);
const inAdmin = computed(() => route.path.startsWith('/admin'));

function isTabActive(to: string): boolean {
  return to === '/' ? route.path === '/' : route.path.startsWith(to);
}

/** Полный список разделов для бургер-меню (быстрый доступ из хедера). */
const navSections = computed<NavItem[]>(() => {
  const base: NavItem[] = [
    { to: '/', icon: Home, labelKey: 'nav.dashboard' },
    { to: '/events', icon: CalendarDays, labelKey: 'nav.events' },
    { to: '/services', icon: LayoutGrid, labelKey: 'nav.services' },
    { to: '/footprint', icon: Activity, labelKey: 'nav.footprint' },
    { to: '/profile', icon: User, labelKey: 'nav.profile' },
  ];
  if (isAdmin.value) {
    base.push({ to: '/admin/services', icon: Shield, labelKey: 'nav.admin' });
  }
  return base;
});

const userInitials = computed(() => {
  const name = authStore.user?.displayName ?? authStore.user?.email ?? '';
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
  }
  return (name[0] ?? '?').toUpperCase();
});

const menuOpen = ref(false);
const menuRef = ref<HTMLElement | null>(null);

const navOpen = ref(false);
const navRef = ref<HTMLElement | null>(null);

function toggleMenu() {
  navOpen.value = false;
  menuOpen.value = !menuOpen.value;
}

function closeMenu() {
  menuOpen.value = false;
}

function toggleNav() {
  menuOpen.value = false;
  navOpen.value = !navOpen.value;
}

function closeNav() {
  navOpen.value = false;
}

function handleLogout() {
  closeMenu();
  void authStore.logout();
}

function handleDocumentClick(event: MouseEvent) {
  const target = event.target as Node | null;
  if (!target) return;
  if (menuOpen.value && menuRef.value && !menuRef.value.contains(target)) {
    closeMenu();
  }
  if (navOpen.value && navRef.value && !navRef.value.contains(target)) {
    closeNav();
  }
}

function handleKey(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    closeMenu();
    closeNav();
  }
}

onMounted(() => {
  document.addEventListener('click', handleDocumentClick);
  document.addEventListener('keydown', handleKey);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick);
  document.removeEventListener('keydown', handleKey);
});
</script>

<template>
  <div class="app-shell">
    <header class="app-shell__topbar">
      <div class="app-shell__left">
        <RouterLink to="/" class="app-shell__brand" :aria-label="t('app.name')">
          <span class="app-shell__logo">
            <img :src="logoZnak" alt="" class="app-shell__logo-mark" />
          </span>
          <span class="app-shell__brand-name">{{ t('app.name') }}</span>
        </RouterLink>

        <div ref="navRef" class="app-shell__nav">
          <button
            type="button"
            class="app-shell__burger"
            :aria-expanded="navOpen"
            aria-haspopup="menu"
            :aria-label="t('nav.menu')"
            @click="toggleNav"
          >
            <Menu :size="20" :stroke-width="1.75" />
          </button>

          <div v-if="navOpen" class="app-shell__nav-drop" role="menu">
            <RouterLink
              v-for="item in navSections"
              :key="item.to"
              :to="item.to"
              class="app-shell__nav-item"
              :class="{ 'app-shell__nav-item--active': isTabActive(item.to) }"
              role="menuitem"
              @click="closeNav"
            >
              <component :is="item.icon" :size="18" :stroke-width="1.75" />
              <span>{{ t(item.labelKey) }}</span>
            </RouterLink>
          </div>
        </div>
      </div>

      <div class="app-shell__topbar-actions">
        <div ref="menuRef" class="app-shell__user-menu">
          <button
            type="button"
            class="app-shell__user-trigger"
            :aria-expanded="menuOpen"
            aria-haspopup="menu"
            @click="toggleMenu"
          >
            <span class="app-shell__avatar">{{ userInitials }}</span>
            <span class="app-shell__user-meta">
              <span class="app-shell__user-name">{{ authStore.user?.displayName ?? '—' }}</span>
              <span class="app-shell__user-email">{{ authStore.user?.email ?? '' }}</span>
            </span>
            <ChevronDown :size="16" :stroke-width="1.75" class="app-shell__caret" />
          </button>

          <div v-if="menuOpen" class="app-shell__dropdown" role="menu">
            <RouterLink to="/profile" class="app-shell__dropdown-item" role="menuitem" @click="closeMenu">
              <User :size="16" :stroke-width="1.75" />
              <span>{{ t('nav.profile') }}</span>
            </RouterLink>
            <RouterLink to="/footprint" class="app-shell__dropdown-item" role="menuitem" @click="closeMenu">
              <Activity :size="16" :stroke-width="1.75" />
              <span>{{ t('nav.footprint') }}</span>
            </RouterLink>
            <RouterLink
              v-if="isAdmin"
              to="/admin/services"
              class="app-shell__dropdown-item"
              role="menuitem"
              @click="closeMenu"
            >
              <Shield :size="16" :stroke-width="1.75" />
              <span>{{ t('nav.admin') }}</span>
            </RouterLink>
            <div class="app-shell__dropdown-sep" role="separator" />
            <button
              type="button"
              class="app-shell__dropdown-item app-shell__dropdown-item--danger"
              role="menuitem"
              @click="handleLogout"
            >
              <LogOut :size="16" :stroke-width="1.75" />
              <span>{{ t('auth.logout') }}</span>
            </button>
          </div>
        </div>
      </div>
    </header>

    <div
      v-if="authStore.isAuthenticated && !authStore.emailVerified"
      class="app-shell__verify"
      role="status"
    >
      <span class="app-shell__verify-text">{{ t('verifyBanner.text') }}</span>
      <button
        v-if="!resent"
        type="button"
        class="app-shell__verify-btn"
        :disabled="resending"
        @click="resendVerification"
      >
        {{ resending ? t('verifyBanner.sending') : t('verifyBanner.resend') }}
      </button>
      <span v-else class="app-shell__verify-sent">{{ t('verifyBanner.sent') }}</span>
    </div>

    <div class="app-shell__body">
      <aside v-if="isAdmin && inAdmin" class="app-shell__subpanel" aria-label="Administration">
        <div class="app-shell__subpanel-head">
          <span class="app-shell__subpanel-eyebrow">{{ t('nav.admin') }}</span>
        </div>
        <nav class="app-shell__subpanel-nav">
          <RouterLink
            v-for="item in adminNav"
            :key="item.to"
            :to="item.to"
            class="app-shell__subpanel-item"
            active-class="app-shell__subpanel-item--active"
          >
            <component :is="item.icon" :size="16" :stroke-width="1.75" />
            <span>{{ t(item.labelKey) }}</span>
          </RouterLink>
        </nav>
      </aside>

      <main class="app-shell__content">
        <slot />
      </main>
    </div>

    <nav class="app-shell__tabbar" aria-label="Main navigation">
      <RouterLink
        v-for="item in tabItems"
        :key="item.to"
        :to="item.to"
        class="app-shell__tab"
        :class="{ 'app-shell__tab--active': isTabActive(item.to) }"
      >
        <component :is="item.icon" :size="22" :stroke-width="1.75" />
        <span class="app-shell__tab-label">{{ t(item.labelKey) }}</span>
      </RouterLink>
    </nav>
  </div>
</template>

<style scoped>
.app-shell {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: var(--color-bg-surface);
}

/* ------------------ Top bar -------------------------------------------- */

.app-shell__topbar {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  height: 56px;
  padding: 0 var(--space-5);
  /* Тёплая полупрозрачность вместо чисто белой: на тёплой бумаге белая
     шапка выглядит наклейкой из другого макета. */
  background: color-mix(in srgb, var(--color-bg-surface) 88%, transparent);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--color-border-subtle);
}

.app-shell__left {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  min-width: 0;
}

.app-shell__brand {
  display: inline-flex;
  align-items: center;
  gap: var(--space-3);
  color: var(--color-text);
  text-decoration: none;
}

.app-shell__brand:hover {
  text-decoration: none;
}

.app-shell__logo {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
  background: linear-gradient(135deg, #4d5dca, #2e3ca5);
  display: flex;
  align-items: center;
  justify-content: center;
}

.app-shell__logo-mark {
  width: 20px;
  height: 20px;
  display: block;
}

/* ------------------ Burger nav ----------------------------------------- */

.app-shell__nav {
  position: relative;
}

.app-shell__burger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: 1px solid transparent;
  background: transparent;
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: background var(--transition-fast), border-color var(--transition-fast),
    color var(--transition-fast);
}

.app-shell__burger:hover {
  background: var(--color-bg-soft);
  border-color: var(--color-border-subtle);
  color: var(--color-text);
}

.app-shell__burger[aria-expanded='true'] {
  background: var(--color-bg-soft);
  border-color: var(--color-border);
  color: var(--color-text);
}

.app-shell__nav-drop {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  min-width: 240px;
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  padding: var(--space-1);
  z-index: var(--z-dropdown);
}

.app-shell__nav-item {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-sm);
  color: var(--color-text-secondary);
  text-decoration: none;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  transition: background var(--transition-fast), color var(--transition-fast);
}

.app-shell__nav-item:hover {
  background: var(--color-bg-soft);
  color: var(--color-text);
  text-decoration: none;
}

/* Активный раздел — заливкой, а не только цветом текста: одним оттенком
   среди четырёх пунктов текущий раздел находится не сразу, особенно на
   ходу. */
.app-shell__nav-item--active {
  background: var(--color-primary-soft);
  color: var(--color-primary);
}

.app-shell__nav-item--active:hover {
  color: var(--color-primary);
}

.app-shell__brand-name {
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-md);
  letter-spacing: var(--letter-spacing-tight);
}

.app-shell__topbar-actions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

/* ------------------ User menu ------------------------------------------ */

.app-shell__user-menu {
  position: relative;
}

.app-shell__user-trigger {
  display: inline-flex;
  align-items: center;
  gap: var(--space-3);
  padding: 4px 8px 4px 4px;
  border: 1px solid transparent;
  background: transparent;
  border-radius: var(--radius-full);
  cursor: pointer;
  color: var(--color-text);
  transition: background var(--transition-fast), border-color var(--transition-fast);
}

.app-shell__user-trigger:hover {
  background: var(--color-bg-soft);
  border-color: var(--color-border-subtle);
}

.app-shell__user-trigger[aria-expanded='true'] {
  background: var(--color-bg-soft);
  border-color: var(--color-border);
}

.app-shell__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: var(--radius-full);
  background: var(--color-primary-soft);
  color: var(--color-primary);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
  flex-shrink: 0;
}

.app-shell__user-meta {
  display: none;
  flex-direction: column;
  text-align: left;
  min-width: 0;
  max-width: 180px;
}

.app-shell__user-name {
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.app-shell__user-email {
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.app-shell__caret {
  display: none;
  color: var(--color-text-muted);
}

@media (min-width: 720px) {
  .app-shell__user-meta,
  .app-shell__caret {
    display: inline-flex;
  }
}

.app-shell__dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 220px;
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  padding: var(--space-1);
  z-index: var(--z-dropdown);
}

.app-shell__dropdown-item {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-sm);
  width: 100%;
  background: transparent;
  border: none;
  text-align: left;
  font-family: inherit;
  font-size: var(--font-size-base);
  color: var(--color-text);
  text-decoration: none;
  cursor: pointer;
  transition: background var(--transition-fast), color var(--transition-fast);
}

.app-shell__dropdown-item:hover {
  background: var(--color-bg-soft);
  color: var(--color-text);
  text-decoration: none;
}

.app-shell__dropdown-item--danger {
  color: var(--color-danger);
}

.app-shell__dropdown-item--danger:hover {
  background: var(--color-danger-soft);
  color: var(--color-danger-hover);
}

.app-shell__dropdown-sep {
  height: 1px;
  margin: var(--space-1) var(--space-1);
  background: var(--color-border-subtle);
}

/* ------------------ Verify-email banner -------------------------------- */

.app-shell__verify {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-5);
  background: var(--color-warning-soft, #fff4e5);
  color: var(--color-warning-hover, #8a5a00);
  border-bottom: 1px solid var(--color-border-subtle);
  font-size: var(--font-size-sm);
}

.app-shell__verify-btn {
  border: 1px solid currentColor;
  background: transparent;
  color: inherit;
  font: inherit;
  font-weight: var(--font-weight-semibold);
  padding: 2px 12px;
  border-radius: var(--radius-full);
  cursor: pointer;
}

.app-shell__verify-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.app-shell__verify-sent {
  font-weight: var(--font-weight-semibold);
}

/* ------------------ Body: (admin sub-panel) + content ------------------ */

.app-shell__body {
  display: flex;
  flex: 1;
  min-height: 0;
}

/* ------------------ Admin sub-panel ------------------------------------ */

.app-shell__subpanel {
  position: sticky;
  top: 56px;
  align-self: flex-start;
  height: calc(100vh - 56px);
  width: 220px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-4) var(--space-3);
  background: var(--color-bg-soft);
  border-right: 1px solid var(--color-border-subtle);
}

.app-shell__subpanel-head {
  padding: 0 var(--space-2);
}

.app-shell__subpanel-eyebrow {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: var(--letter-spacing-uppercase);
  color: var(--color-text-muted);
}

.app-shell__subpanel-nav {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.app-shell__subpanel-item {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  text-decoration: none;
  font-weight: var(--font-weight-medium);
  font-size: var(--font-size-base);
  transition: background var(--transition-fast), color var(--transition-fast);
}

.app-shell__subpanel-item:hover {
  background: var(--color-bg-muted);
  color: var(--color-text);
  text-decoration: none;
}

.app-shell__subpanel-item--active {
  background: var(--color-bg);
  color: var(--color-primary);
  box-shadow: var(--shadow-card);
}

/* ------------------ Content -------------------------------------------- */

.app-shell__content {
  flex: 1;
  padding: var(--space-8) var(--space-6);
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  min-width: 0;
}

/* ------------------ Bottom tab bar (mobile only) ----------------------- */

.app-shell__tabbar {
  display: none;
}

@media (max-width: 959px) {
  .app-shell__tabbar {
    display: flex;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 30;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    border-top: 1px solid var(--color-border-subtle);
    padding-bottom: env(safe-area-inset-bottom, 0);
  }

  .app-shell__content {
    padding-bottom: calc(64px + var(--space-6) + env(safe-area-inset-bottom, 0px));
  }

  /* На мобилке админ-сабпанель не показываем — вход в разделы из меню/админки */
  .app-shell__subpanel {
    display: none;
  }
}

.app-shell__tab {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  height: 60px;
  color: var(--color-text-muted);
  text-decoration: none;
  transition: color var(--transition-fast);
}

.app-shell__tab:hover {
  text-decoration: none;
}

/* На телефоне активная вкладка получает и цвет, и подложку под иконкой:
   строка из четырёх подписей по 11px иначе читается как одна серая полоса. */
.app-shell__tab--active {
  color: var(--color-primary);
}

.app-shell__tab--active svg {
  border-radius: var(--radius-full);
  background: var(--color-primary-soft);
  box-shadow: 0 0 0 6px var(--color-primary-soft);
}

.app-shell__tab-label {
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  line-height: 1;
}
</style>
